import React from 'react';
import { PerformanceAnalysisProps } from '../../types/score';
import { getDisplayDomain } from '../../utils/domainUtils';
import { 
  Zap, 
  Search,
  Eye,
  Settings,
  Bug,
  BarChart3,
  Clock,
  Target,
  AlertTriangle
} from 'lucide-react';

const PerformanceAnalysisSection: React.FC<PerformanceAnalysisProps> = ({ 
  performanceLoading,
  performanceError,
  performanceAnalysis,
  activeSection,
  setActiveSection,
  siteUrl,
  performanceMetrics
}) => {
  // Utiliser les données dynamiques ou les valeurs par défaut
  const metrics = performanceMetrics || {
    globalScore: 83,
    performanceScore: 90,
    seoScore: 67,
    uxScore: 88,
    technicalScore: 100,
    loadingTime: '1.2s',
    firstContentfulPaint: '0.8s',
    largestContentfulPaint: '2.1s',
    criticalIssues: 1,
    warnings: 4,
    analysisDate: new Date().toLocaleDateString('fr-FR')
  };
  return (
    <div className="mt-8 mb-8">
      <div className="bg-gradient-to-br from-gray-50 to-slate-100 rounded-xl p-8">

        {/* Navigation des sections d'analyse */}
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
              {[
                { id: 'overview', label: 'Vue d\'ensemble', icon: BarChart3, color: 'from-blue-500 to-indigo-500' },
                { id: 'performance', label: 'Performance', icon: Zap, color: 'from-green-500 to-emerald-500' },
                { id: 'seo', label: 'SEO & Contenu', icon: Search, color: 'from-orange-500 to-red-500' },
                { id: 'technical', label: 'Technique', icon: Settings, color: 'from-indigo-500 to-purple-500' },
                { id: 'issues', label: 'Problèmes', icon: Bug, color: 'from-red-500 to-pink-500' }
              ].map((item, index) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveSection(item.id)}
                    className={`p-4 text-center transition-all duration-300 hover:bg-gray-50 ${
                      isActive ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-b-2 border-blue-500' : ''
                    }`}
                    style={{animationDelay: `${index * 50}ms`}}
                  >
                    <div className={`w-8 h-8 mx-auto mb-2 rounded-lg bg-gradient-to-br ${item.color} p-1.5 text-white`}>
                      <Icon size={20} />
                    </div>
                    <div className={`text-xs font-medium ${isActive ? 'text-blue-700' : 'text-gray-600'}`}>
                      {item.label}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Contenu des sections d'analyse */}
        <div className="space-y-8">
          {/* Vue d'ensemble */}
          {activeSection === 'overview' && (
            <div className="space-y-8 animate-fadeSlideUp">
              {/* Scores par catégorie */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { key: 'performance', score: metrics.performanceScore, icon: Zap, color: 'from-green-500 to-emerald-500', issues: 2 },
                  { key: 'seo', score: metrics.seoScore, icon: Search, color: 'from-blue-500 to-indigo-500', issues: 3 },
                  { key: 'ux', score: metrics.uxScore, icon: Eye, color: 'from-purple-500 to-fuchsia-500', issues: 1 },
                  { key: 'technical', score: metrics.technicalScore, icon: Settings, color: 'from-indigo-500 to-purple-500', issues: 0 }
                ].map((category, index) => {
                  const Icon = category.icon;
                  
                  return (
                    <div key={category.key} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-md" style={{animationDelay: `${index * 100}ms`}}>
                      <div className={`p-1 bg-gradient-to-r ${category.color}`}></div>
                      <div className="p-6 text-center">
                        <div className={`w-12 h-12 mx-auto mb-4 rounded-lg bg-gradient-to-br ${category.color} p-2.5 text-white`}>
                          <Icon size={24} />
                        </div>
                        <div className="text-3xl font-bold text-gray-900 mb-1">
                          {category.score}
                        </div>
                        <div className="text-sm text-gray-600 mb-2 capitalize">
                          {category.key === 'ux' ? 'UX Design' : category.key}
                        </div>
                        <div className={`inline-block px-2 py-1 text-xs font-bold text-white rounded-full bg-gradient-to-r ${
                          category.score >= 90 ? 'from-green-500 to-emerald-500' :
                          category.score >= 70 ? 'from-blue-500 to-indigo-500' :
                          category.score >= 50 ? 'from-orange-500 to-yellow-500' :
                          'from-red-500 to-pink-500'
                        }`}>
                          Grade {category.score >= 90 ? 'A' : category.score >= 70 ? 'B' : category.score >= 50 ? 'C' : 'D'}
                        </div>
                        <div className="text-xs text-gray-500 mt-2">
                          {category.issues} problème{category.issues > 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Recommandations prioritaires */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-1 bg-gradient-to-r from-purple-500 to-fuchsia-500"></div>
                <div className="p-6">
                  <div className="flex items-center mb-6">
                    <div className="p-2 rounded-lg bg-purple-50 text-purple-700 mr-4">
                      <Target size={20} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Actions Prioritaires</h3>
                      <p className="text-gray-600">Recommandations à fort impact pour améliorer votre score</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                      {
                        id: 1,
                        title: "Optimiser les images",
                        description: "Compresser et redimensionner les images pour améliorer les temps de chargement",
                        impact: "high",
                        effort: "low",
                        timeEstimate: "2-4 heures",
                        businessImpact: "Amélioration SEO et UX"
                      },
                      {
                        id: 2,
                        title: "Améliorer les métadonnées",
                        description: "Optimiser les balises title et meta description pour le référencement",
                        impact: "medium",
                        effort: "low",
                        timeEstimate: "1-2 heures",
                        businessImpact: "Meilleur positionnement Google"
                      },
                      {
                        id: 3,
                        title: "Mise en cache navigateur",
                        description: "Configurer la mise en cache pour réduire les temps de chargement",
                        impact: "high",
                        effort: "medium",
                        timeEstimate: "4-6 heures",
                        businessImpact: "Performance utilisateur"
                      }
                    ].map((recommendation, index) => (
                      <div key={recommendation.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-center mb-3">
                          <div className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                            {index + 1}
                          </div>
                          <h4 className="font-semibold text-gray-900 text-sm">{recommendation.title}</h4>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{recommendation.description}</p>
                        <div className="flex gap-2 mb-3">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            recommendation.impact === 'high' ? 'bg-red-100 text-red-700' :
                            recommendation.impact === 'medium' ? 'bg-orange-100 text-orange-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            Impact {recommendation.impact}
                          </span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${
                            recommendation.effort === 'high' ? 'border-red-200 text-red-700' :
                            recommendation.effort === 'medium' ? 'border-orange-200 text-orange-700' :
                            'border-green-200 text-green-700'
                          }`}>
                            Effort {recommendation.effort}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          <div className="flex items-center mb-1">
                            <Clock size={12} className="mr-1" />
                            {recommendation.timeEstimate}
                          </div>
                          <div className="font-medium text-purple-600">
                            {recommendation.businessImpact}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Performance */}
          {activeSection === 'performance' && (
            <div className="space-y-8 animate-fadeSlideUp">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-1 bg-gradient-to-r from-green-500 to-emerald-500"></div>
                <div className="p-6">
                  <div className="flex items-center mb-6">
                    <div className="p-2 rounded-lg bg-green-50 text-green-700 mr-4">
                      <Zap size={20} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Métriques de Performance</h3>
                      <p className="text-gray-600">Analyse détaillée des temps de chargement et Core Web Vitals</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{metrics.loadingTime}</div>
                      <div className="text-sm text-gray-600">Temps de chargement</div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{metrics.firstContentfulPaint}</div>
                      <div className="text-sm text-gray-600">First Contentful Paint</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">{metrics.largestContentfulPaint}</div>
                      <div className="text-sm text-gray-600">Largest Contentful Paint</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SEO & Contenu */}
          {activeSection === 'seo' && (
            <div className="space-y-8 animate-fadeSlideUp">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-1 bg-gradient-to-r from-orange-500 to-red-500"></div>
                <div className="p-6">
                  <div className="flex items-center mb-6">
                    <div className="p-2 rounded-lg bg-orange-50 text-orange-700 mr-4">
                      <Search size={20} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Informations Meta & Contenu</h3>
                      <p className="text-gray-600">Analyse du contenu et des métadonnées pour le référencement</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">Balise Title</span>
                      <span className="text-sm text-green-600">✓ Optimisée</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">Meta Description</span>
                      <span className="text-sm text-orange-600">⚠ À améliorer</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">Balises H1-H6</span>
                      <span className="text-sm text-green-600">✓ Bien structurées</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Technique */}
          {activeSection === 'technical' && (
            <div className="animate-fadeSlideUp">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-1 bg-gradient-to-r from-indigo-500 to-purple-500"></div>
                <div className="p-6">
                  <div className="flex items-center mb-6">
                    <div className="p-2 rounded-lg bg-indigo-50 text-indigo-700 mr-4">
                      <Settings size={20} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Informations Techniques</h3>
                      <p className="text-gray-600">Détails techniques sur la configuration et les performances du serveur</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium">HTTPS</span>
                        <span className="text-sm text-green-600">✓ Activé</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium">Compression GZIP</span>
                        <span className="text-sm text-green-600">✓ Activée</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium">Version HTTP</span>
                        <span className="text-sm text-blue-600">HTTP/2</span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium">Temps de réponse serveur</span>
                        <span className="text-sm text-green-600">180ms</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium">CDN</span>
                        <span className="text-sm text-orange-600">⚠ Non configuré</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium">Cache navigateur</span>
                        <span className="text-sm text-green-600">✓ Configuré</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Problèmes */}
          {activeSection === 'issues' && (
            <div className="animate-fadeSlideUp">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-1 bg-gradient-to-r from-red-500 to-pink-500"></div>
                <div className="p-6">
                  <div className="flex items-center mb-6">
                    <div className="p-2 rounded-lg bg-red-50 text-red-700 mr-4">
                      <Bug size={20} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Erreurs et Avertissements</h3>
                      <p className="text-gray-600">Erreurs et avertissements détectés dans le code HTML/CSS</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {/* Afficher les erreurs réelles depuis performanceAnalysis */}
                    {performanceAnalysis?.resource_errors?.errors?.map((error, index) => (
                      <div key={index} className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                        <div className="flex items-center mb-2">
                          <AlertTriangle size={16} className="text-red-600 mr-2" />
                          <span className="font-medium text-red-800">Erreur critique</span>
                        </div>
                        <p className="text-sm text-red-700">{error.message}</p>
                        {error.line && (
                          <p className="text-xs text-red-600 mt-1">Ligne {error.line}, Colonne {error.column}</p>
                        )}
                      </div>
                    ))}
                    
                    {/* Afficher les avertissements réels */}
                    {performanceAnalysis?.resource_errors?.warnings?.map((warning, index) => (
                      <div key={index} className="border-l-4 border-orange-500 bg-orange-50 p-4 rounded-r-lg">
                        <div className="flex items-center mb-2">
                          <AlertTriangle size={16} className="text-orange-600 mr-2" />
                          <span className="font-medium text-orange-800">Avertissement</span>
                        </div>
                        <p className="text-sm text-orange-700">{warning.message}</p>
                        {warning.line && (
                          <p className="text-xs text-orange-600 mt-1">Ligne {warning.line}, Colonne {warning.column}</p>
                        )}
                      </div>
                    ))}

                    {/* Fallback si pas de données */}
                    {(!performanceAnalysis?.resource_errors?.errors?.length && !performanceAnalysis?.resource_errors?.warnings?.length) && (
                      <div className="text-center py-8 text-gray-500">
                        <Bug size={48} className="mx-auto mb-4 opacity-50" />
                        <p>Aucune erreur ou avertissement détecté</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PerformanceAnalysisSection;
